import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Users, FileText, Receipt, LayoutDashboard, Settings, Package, Shield, LogOut, User, Menu, X, Trash2, MessageSquare, HelpCircle, PlayCircle, BarChart3, Lightbulb, Moon, Sun, FolderKanban, Car, UserCheck, ReceiptText, Award, Lock, CalendarDays, CalendarCheck, Globe, Mic, AlertTriangle, Voicemail, CalendarClock, Inbox } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { useToast } from "../hooks/use-toast";
import api from "../utils/api";
import { useFeatureFlag } from "../utils/featureFlags";
import { HardHat } from "lucide-react";
import SupportDialog from "./SupportDialog";
import WalkthroughGuide from "./WalkthroughGuide";
import GuidedTour from "./GuidedTour";
import DemoBanner from "./DemoBanner";
import TrialExpiredModal from "./TrialExpiredModal";
import CancellationBanner from "./CancellationBanner";
import PaymentFailedBanner from "./PaymentFailedBanner";
import VoiceAllowanceIntroModal from "./VoiceAllowanceIntroModal";
import CalendarWatchNotice from "./CalendarWatchNotice";
import WhatsNewPopup from "./WhatsNewPopup";
import OnboardingNudge from "./OnboardingNudge";
import WhatsNewButton from "./WhatsNewButton";
import SyncStatusChip from "./offline/SyncStatusChip";
import { startDrainWorker } from "../lib/offlineQueue/drain";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { toast } = useToast();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [openTickets, setOpenTickets] = useState(0);
  const [quoteBadge, setQuoteBadge] = useState(0);
  const [invoiceBadge, setInvoiceBadge] = useState(0);
  const [enquiryBadge, setEnquiryBadge] = useState(0);
  const [inviteBadge, setInviteBadge] = useState(0); // pending build/job invites assigned to me
  const [enquiryTotal, setEnquiryTotal] = useState(0);
  const [inviteTotal, setInviteTotal] = useState(0);
  const [leadTotal, setLeadTotal] = useState(0);
  const [emergencyEnabled, setEmergencyEnabled] = useState(false);
  const [emergencyBadge, setEmergencyBadge] = useState(0);
  const [voicemailBadge, setVoicemailBadge] = useState(0);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  const [featureToggles, setFeatureToggles] = useState({ reports_enabled: true, affiliate_enabled: true, mileage_enabled: false, receipts_enabled: false, dictaphone_enabled: false });
  const [certificatesBetaAccess, setCertificatesBetaAccess] = useState(null);
  const [trialExpired, setTrialExpired] = useState(false);
  const [firmBookingPendingCount, setFirmBookingPendingCount] = useState(0);
  const [firmBookingsAccess, setFirmBookingsAccess] = useState(false);
  const [firmBookingsEnabled, setFirmBookingsEnabled] = useState(false);
  const [jobBookingsCount, setJobBookingsCount] = useState(0); // # active offers
  const [jobBookingsPendingCount, setJobBookingsPendingCount] = useState(0); // # upcoming bookings (sidebar badge)
  const [featureRequestsBadge, setFeatureRequestsBadge] = useState(0);

  // Job Bookings V2 — Phase D unified diary visibility (flag-gated)
  const v2PhaseDEnabled = useFeatureFlag("job_bookings_v2_phase_d");
  // Project Manager (Builds) — flag-gated availability (admins during dark launch)
  const projectManagerFlag = useFeatureFlag("project_manager");

  // Check Firm Bookings access + enabled state + pending count for sidebar badge.
  // The sidebar entry only appears when the user has tier access AND has
  // toggled the feature on in /settings/firm-bookings — so it's not a dead
  // link for users who haven't set it up yet.
  // Re-runs whenever the route changes (so toggling off in /settings/firm-
  // bookings then navigating away updates the sidebar) AND when a custom
  // `firm-bookings:enabled-changed` event fires (instant update without
  // needing to navigate).
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const access = await api.get("/firm-bookings/access");
        if (cancelled) return;
        const has = !!access.data.has_access;
        const en = !!access.data.enabled;
        setFirmBookingsAccess(has);
        setFirmBookingsEnabled(en);
        if (has && en) {
          const reqs = await api.get("/firm-bookings/requests?status=pending");
          if (!cancelled) setFirmBookingPendingCount(reqs.data.counts?.pending || 0);
        } else {
          setFirmBookingPendingCount(0);
        }
      } catch {}
    };
    refresh();
    const onChange = () => refresh();
    window.addEventListener("firm-bookings:enabled-changed", onChange);
    return () => {
      cancelled = true;
      window.removeEventListener("firm-bookings:enabled-changed", onChange);
    };
  }, [location.pathname]);

  // Sidebar entry for Job Bookings only appears once the user has
  // at least one offer set up — keeps the nav clean for everyone else.
  // Badge shows the count of upcoming bookings so they have a one-glance
  // pulse on what's coming up.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const offers = await api.get("/job-bookings/offers");
        if (cancelled) return;
        const offerCount = (offers.data?.offers || []).length;
        setJobBookingsCount(offerCount);
        if (offerCount > 0) {
          const appts = await api.get("/job-bookings/appointments");
          if (cancelled) return;
          const now = Date.now();
          const upcoming = (appts.data?.appointments || []).filter((a) => {
            // Exclude jobs that have been ticked off via the unified diary.
            if (a.completed_at) return false;
            const t = new Date(a.scheduled_start).getTime();
            return Number.isFinite(t) && t >= now;
          });
          setJobBookingsPendingCount(upcoming.length);
        } else {
          setJobBookingsPendingCount(0);
        }
      } catch {
        // Silent — sidebar should never crash on a transient API error.
      }
    };
    refresh();
    const onChange = () => refresh();
    window.addEventListener("job-bookings:changed", onChange);
    return () => {
      cancelled = true;
      window.removeEventListener("job-bookings:changed", onChange);
    };
  }, [location.pathname]);

  // Check trial status on mount
  useEffect(() => {
    // Start the offline-queue drain worker (idempotent). Replays
    // queued customer writes when the device comes back online.
    startDrainWorker();
    const checkTrialStatus = async () => {
      try {
        const response = await api.get("/auth/me");
        if (response.data.trial_expired) {
          setTrialExpired(true);
        }
      } catch {}
    };
    checkTrialStatus();
  }, []);

  // Fetch feature toggles (global) and mileage settings (per-user)
  useEffect(() => {
    const fetchFeatureToggles = async () => {
      try {
        const response = await api.get("/feature-toggles");
        setFeatureToggles(prev => ({ ...prev, ...response.data }));
      } catch (error) {
        console.error("Error fetching feature toggles:", error);
      }
    };
    const fetchMileageSettings = async () => {
      try {
        const response = await api.get("/mileage/settings");
        setFeatureToggles(prev => ({ ...prev, mileage_enabled: response.data.mileage_enabled }));
      } catch (error) {
        console.error("Error fetching mileage settings:", error);
      }
    };
    const fetchReceiptSettings = async () => {
      try {
        const response = await api.get("/receipts/settings");
        setFeatureToggles(prev => ({ ...prev, receipts_enabled: response.data.enabled }));
      } catch (error) {
        console.error("Error fetching receipt settings:", error);
      }
    };
    fetchFeatureToggles();
    fetchMileageSettings();
    fetchReceiptSettings();
    
    // Fetch dictaphone settings
    const fetchDictaphoneSettings = async () => {
      try {
        const response = await api.get("/dictaphone/settings");
        setFeatureToggles(prev => ({ ...prev, dictaphone_enabled: response.data.enabled }));
      } catch {}
    };
    fetchDictaphoneSettings();

    // Fetch Project Manager per-user opt-in (404 => no access => stays hidden)
    const fetchProjectManagerSettings = async () => {
      try {
        const response = await api.get("/builds/settings");
        setFeatureToggles(prev => ({ ...prev, project_manager_enabled: response.data.enabled }));
      } catch {
        setFeatureToggles(prev => ({ ...prev, project_manager_enabled: false }));
      }
    };
    fetchProjectManagerSettings();

    // Listen for Project Manager toggle changes from Settings page
    const handleProjectManagerToggle = (e) => {
      setFeatureToggles(prev => ({ ...prev, project_manager_enabled: e.detail.enabled }));
    };
    window.addEventListener('project-manager-toggled', handleProjectManagerToggle);

    // Listen for dictaphone toggle changes from Settings page
    const handleDictaphoneToggle = (e) => {
      setFeatureToggles(prev => ({ ...prev, dictaphone_enabled: e.detail.enabled }));
    };
    window.addEventListener('dictaphone-toggled', handleDictaphoneToggle);
    
    // Fetch certificates beta access
    const fetchCertificatesBetaAccess = async () => {
      try {
        const response = await api.get("/certificates/beta-access");
        setCertificatesBetaAccess(response.data);
      } catch (error) {
        // If endpoint doesn't exist yet, assume no access
        setCertificatesBetaAccess({ has_access: false });
      }
    };
    fetchCertificatesBetaAccess();
    
    // Listen for receipts toggle changes from Settings page
    const handleReceiptsToggle = (e) => {
      setFeatureToggles(prev => ({ ...prev, receipts_enabled: e.detail.enabled }));
    };
    window.addEventListener('receipts-toggled', handleReceiptsToggle);
    
    return () => {
      window.removeEventListener('receipts-toggled', handleReceiptsToggle);
      window.removeEventListener('dictaphone-toggled', handleDictaphoneToggle);
      window.removeEventListener('project-manager-toggled', handleProjectManagerToggle);
    };
  }, []);

  // Fetch notification badges for quotes, invoices, and page enquiries
  useEffect(() => {
    const fetchNotificationBadges = async () => {
      try {
        const response = await api.get("/notifications/badges");
        setQuoteBadge(response.data.quotes || 0);
        setInvoiceBadge(response.data.invoices || 0);
      } catch (error) {
        console.error("Error fetching notification badges:", error);
      }
    };
    const fetchEnquiryBadge = async () => {
      try {
        const response = await api.get("/pages/enquiries/count");
        setEnquiryBadge(response.data.new_count || 0);
        setEnquiryTotal(response.data.total ?? response.data.total_count ?? 0);
      } catch {}
    };
    const fetchInviteBadge = async () => {
      try {
        const response = await api.get("/build-invites/badge");
        setInviteBadge(response.data.count || 0);
        setInviteTotal(response.data.total || 0);
      } catch {}
    };
    const fetchLeadTotal = async () => {
      try {
        const response = await api.get("/directory/my-leads/count");
        setLeadTotal(response.data.total_count || 0);
      } catch {}
    };
    const fetchEmergencyBadge = async () => {
      try {
        const response = await api.get("/voice-receptionist/emergency-callouts/badge");
        setEmergencyEnabled(!!response.data.enabled);
        setEmergencyBadge(response.data.new_count || 0);
      } catch {}
    };
    const fetchVoicemailBadge = async () => {
      try {
        const response = await api.get("/voice-receptionist/voicemails/badge");
        setVoicemailBadge(response.data.count || 0);
      } catch {}
    };
    // Feature Requests badge — admins see new user replies + new
    // requests; users see new admin replies on their own threads.
    // When count goes UP between polls (i.e. something new arrived
    // while the user wasn't on the page) fire a toast so the
    // notification surfaces even if they're not looking at the sidebar.
    const fetchFeatureRequestsBadge = async () => {
      try {
        const response = await api.get("/feature-requests/unread-count");
        const next = response.data.count || 0;
        setFeatureRequestsBadge((prev) => {
          // Only toast on a real INCREASE (not initial mount, and not
          // when navigating to /feature-requests which auto-resets to 0).
          if (next > prev && prev > 0 && location.pathname !== "/feature-requests") {
            const delta = next - prev;
            toast({
              title: `${delta} new ${delta === 1 ? "reply" : "replies"} on Feature Requests`,
              description: "Open the lightbulb in the sidebar to read.",
            });
          }
          return next;
        });
      } catch {}
    };
    
    fetchNotificationBadges();
    fetchEnquiryBadge();
    fetchInviteBadge();
    fetchLeadTotal();
    fetchEmergencyBadge();
    fetchVoicemailBadge();
    fetchFeatureRequestsBadge();
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchNotificationBadges();
      fetchEnquiryBadge();
      fetchInviteBadge();
      fetchLeadTotal();
      fetchEmergencyBadge();
      fetchVoicemailBadge();
      fetchFeatureRequestsBadge();
    }, 30000);
    return () => clearInterval(interval);
  }, [location.pathname]); // Refresh when navigating

  // Fetch open support ticket count for admins
  useEffect(() => {
    if (isAdmin) {
      const fetchSupportStats = async () => {
        try {
          const response = await api.get("/admin/support/stats");
          setOpenTickets(response.data.open || 0);
        } catch (error) {
          console.error("Error fetching support stats:", error);
        }
      };
      
      fetchSupportStats();
      // Refresh every 60 seconds
      const interval = setInterval(fetchSupportStats, 60000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  // Check if user is a restricted org member (cert_user or staff - not owner)
  const isRestrictedOrgMember = user?.org_role && user.org_role !== "owner";

  // Navigation items - restricted for cert_user/staff (certificates only)
  const navItems = isRestrictedOrgMember ? [
    // Restricted navigation for cert_user and office staff
    { path: "/certificates/my-schedule", label: "My Schedule", icon: CalendarDays },
    { path: "/certificates", label: "Certificates", icon: Award },
    { path: "/settings", label: "Settings", icon: Settings },
  ] : [
    // --- Work ---
    { type: "group", label: "Work" },
    { path: "/", label: "Dashboard", icon: LayoutDashboard, tourId: "dashboard" },
    ...((enquiryTotal + inviteTotal + leadTotal) > 0 ? [{ path: "/enquiries", label: "Inbox", icon: Inbox, badge: (enquiryBadge + inviteBadge) > 0 ? (enquiryBadge + inviteBadge) : null, badgeColor: "blue" }] : []),
    { path: "/customers", label: "Customers", icon: Users, tourId: "customers" },
    { path: "/quotes", label: "Quotes/Estimates", icon: FileText, badge: quoteBadge, badgeColor: "green", tourId: "quotes" },
    { path: "/invoices", label: "Invoices", icon: Receipt, badge: invoiceBadge, badgeColor: "green", tourId: "invoices" },
    // --- Tools ---
    { type: "group", label: "Tools" },
    { path: "/items", label: "Items", icon: Package, tourId: "items" },
    ...(featureToggles.dictaphone_enabled ? [{ path: "/dictaphone", label: "Dictaphone", icon: Mic }] : []),
    ...(featureToggles.mileage_enabled ? [{ path: "/mileage", label: "Mileage", icon: Car }] : []),
    { path: "/my-page", label: "My Page", icon: Globe, badge: enquiryBadge, badgeColor: "blue" },
    ...(firmBookingsAccess && firmBookingsEnabled ? [{ path: "/firm-bookings/inbox", label: "Firm Bookings", icon: CalendarDays, badge: firmBookingPendingCount > 0 ? firmBookingPendingCount : null, badgeColor: "amber" }] : []),
    ...(jobBookingsCount > 0 ? [{ path: "/job-bookings/bookings", label: "Job Bookings", icon: CalendarCheck, badge: jobBookingsPendingCount > 0 ? jobBookingsPendingCount : null, badgeColor: "emerald" }] : []),
    ...(v2PhaseDEnabled ? [{ path: "/schedule", label: "Schedule", icon: CalendarClock }] : []),
    ...(emergencyEnabled ? [{ path: "/emergency-callouts", label: "Emergency Callouts", icon: AlertTriangle, badge: emergencyBadge || null, badgeColor: "red" }] : []),
    ...(voicemailBadge > 0 ? [{ path: "/voicemails", label: "Voicemails", icon: Voicemail, badge: voicemailBadge, badgeColor: "blue" }] : []),
    // --- Business ---
    { type: "group", label: "Business" },
    ...[{ path: "/reports", label: "Reports", icon: BarChart3, tourId: "reports" }],
    ...(featureToggles.receipts_enabled ? [{ path: "/receipts", label: "Receipts", icon: ReceiptText }] : []),
    // --- Account ---
    { type: "group", label: "Account" },
    { path: "/settings", label: "Settings", icon: Settings, tourId: "settings" },
    { path: "/feature-requests", label: "Feature Requests", icon: Lightbulb, badge: featureRequestsBadge > 0 ? featureRequestsBadge : null, badgeColor: "red", tourId: "features" },
  ];

  // Add admin links if user is admin (not for restricted org members)
  // Projects available to admin OR Pro/Elite/Legacy tier users
  const canAccessProjects = !isRestrictedOrgMember && (isAdmin || ['pro', 'elite', 'legacy'].includes(user?.tier));
  // Project Manager (Builds): Pro+ paid (pro/elite) + Legacy grandfathered +
  // Beta/Crew testers + admins. Mirrors backend _PM_ALLOWED_TIERS.
  const canAccessProjectManager = !isRestrictedOrgMember && (isAdmin || ['pro', 'elite', 'legacy', 'beta', 'crew'].includes(user?.tier));
  
  // Insert Projects and Certificates into "Work" group (after Invoices)
  if (!isRestrictedOrgMember) {
    const toolsIdx = navItems.findIndex(i => i.type === "group" && i.label === "Tools");
    const insertAt = toolsIdx > -1 ? toolsIdx : navItems.length;
    const workItems = [];
    
    if (canAccessProjects) {
      workItems.push({ path: "/proposals", label: "Proposals", icon: FolderKanban });
    }

    // Project Manager (Builds): sits directly under Proposals. Requires the
    // feature flag (admins during dark launch), Pro+/tester access, AND the
    // user's per-user opt-in toggle from Settings.
    if (canAccessProjectManager && projectManagerFlag && featureToggles.project_manager_enabled) {
      workItems.push({ path: "/project-manager", label: "Project Manager", icon: HardHat, badge: "NEW", badgeColor: "blue" });
    }
    
    // No more global "feature_live" — module sits behind has_access only.
    const showCertificatesLock = certificatesBetaAccess && !certificatesBetaAccess.has_access;
    workItems.push({ 
      path: "/certificates", 
      label: "Certificates", 
      icon: showCertificatesLock ? Lock : Award
    });
    
    navItems.splice(insertAt, 0, ...workItems);
  }
  
  // Add Admin group
  if (isAdmin && !isRestrictedOrgMember) {
    navItems.push({ type: "group", label: "Admin" });
    navItems.push({ path: "/admin", label: "Admin", icon: Shield });
    navItems.push({ path: "/support", label: "Support", icon: MessageSquare, badge: openTickets, badgeColor: "red" });
  }
  
  // Add ambassador dashboard link for ambassador accounts (not for restricted org members)
  if (!isRestrictedOrgMember && user?.account_type === 'ambassador') {
    navItems.push({ path: "/ambassador", label: "Ambassador", icon: UserCheck, badge: "20%", badgeColor: "purple" });
  }
  
  // Add reseller dashboard link for reseller accounts (not for restricted org members)
  if (!isRestrictedOrgMember && user?.account_type === 'reseller') {
    navItems.push({ path: "/reseller-dashboard", label: "Reseller", icon: UserCheck, badge: "20%", badgeColor: "emerald" });
  }
  
  // Add accountant dashboard link for users who are accountants (but not accountant-only, and not restricted org members)
  if (!isRestrictedOrgMember && user?.is_accountant && !user?.accountant_only) {
    navItems.push({ path: "/accountant", label: "Accountant", icon: UserCheck });
  }

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      // Send the reason in the request body so the deletion archive
      // captures WHY they left. Backend treats body as optional —
      // empty / null is fine but we strongly encourage a reason.
      await api.delete("/auth/account", {
        data: { reason: deleteReason || null },
      });
      toast({
        title: "Account Deleted",
        description: "Your account has been permanently deleted.",
      });
      logout();
      navigate("/landing");
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete account",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteReason("");
    }
  };

  const handleNavClick = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Demo Banner */}
      <DemoBanner />
      {/* GDPR cancellation banner — only renders when account is in cancellation flow */}
      <div className="fixed top-0 left-0 right-0 z-50 lg:relative lg:order-first lg:col-span-full">
        <CancellationBanner />
      </div>
      
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between" style={{ top: user?.is_demo ? '36px' : '0' }}>
        <img 
          src={`${API_URL}/api/uploads/quotesorted_logo.png`} 
          alt="QuoteSorted" 
          className="h-12 w-auto dark:brightness-0 dark:invert"
        />
        <div className="flex items-center gap-1">
          <WhatsNewButton place="mobile" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop always visible, Mobile slide-in */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 relative">
          <img 
            src={`${API_URL}/api/uploads/quotesorted_logo.png`} 
            alt="QuoteSorted" 
            className="h-16 w-auto dark:brightness-0 dark:invert"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Manage with ease</p>
          <div className="absolute top-4 right-4 hidden lg:block">
            <WhatsNewButton place="desktop" />
          </div>
        </div>
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {navItems.map((item, index) => {
              // Render group separator
              if (item.type === "group") {
                return (
                  <li key={`group-${item.label}`} className={index > 0 ? "pt-4" : ""}>
                    <span className="px-4 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                      {item.label}
                    </span>
                  </li>
                );
              }
              
              const Icon = item.icon;
              const badgeColorClass = item.badgeColor === "green"
                ? "bg-green-500"
                : item.badgeColor === "blue"
                ? "bg-blue-500"
                : item.badgeColor === "emerald"
                ? "bg-emerald-500"
                : item.badgeColor === "amber"
                ? "bg-amber-500"
                : "bg-red-500";
              return (
                <li key={item.path}>
                  <button
                    onClick={() => handleNavClick(item.path)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                      isActive(item.path)
                        ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/[\/\s]/g, '-')}`}
                    data-tour={item.tourId ? `nav-${item.tourId}` : undefined}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="flex-1 text-left text-sm">{item.label}</span>
                    {item.badge && typeof item.badge === 'string' ? (
                      <span className={`${item.badgeColor === "purple" ? "bg-purple-100 text-purple-700" : item.badgeColor === "green" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"} text-xs font-bold px-2 py-0.5 rounded-full`}>
                        {item.badge}
                      </span>
                    ) : null}
                    {typeof item.badge === 'number' && item.badge > 0 ? (
                      <span className={`${badgeColorClass} text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center`}>
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
          
          {/* Dark Mode Toggle */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            {/* Offline sync chip — only renders when there's something queued */}
            <div className="px-4 mb-2">
              <SyncStatusChip />
            </div>
            <button
              onClick={toggleDarkMode}
              className="hidden w-full items-center gap-3 px-4 py-3 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
              data-testid="dark-mode-toggle"
            >
              {isDarkMode ? (
                <>
                  <Sun className="w-5 h-5 text-yellow-500" />
                  <span className="flex-1 text-left">Light Mode</span>
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5 text-gray-500" />
                  <span className="flex-1 text-left">Dark Mode</span>
                </>
              )}
            </button>
          </div>
        </nav>

        {/* User Menu */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-tour="user-menu">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-3 dark:text-gray-200 dark:hover:bg-gray-700" data-testid="user-menu-button">
                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 dark:bg-gray-800 dark:border-gray-700">
              <DropdownMenuItem onClick={() => handleNavClick("/settings")} data-testid="menu-settings" className="dark:text-gray-200 dark:hover:bg-gray-700">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => handleNavClick("/admin")} data-testid="menu-admin" className="dark:text-gray-200 dark:hover:bg-gray-700">
                  <Shield className="mr-2 h-4 w-4" />
                  Admin Dashboard
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem onClick={() => handleNavClick("/admin/roadmap")} data-testid="menu-admin-roadmap" className="dark:text-gray-200 dark:hover:bg-gray-700">
                  <Lightbulb className="mr-2 h-4 w-4" />
                  Roadmap Editor
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="dark:bg-gray-700" />
              <DropdownMenuItem onClick={() => setShowGuidedTour(true)} data-testid="menu-take-tour" className="dark:text-gray-200 dark:hover:bg-gray-700">
                <PlayCircle className="mr-2 h-4 w-4" />
                Take a Tour
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowWalkthrough(true)} data-testid="menu-how-it-works" className="dark:text-gray-200 dark:hover:bg-gray-700">
                <HelpCircle className="mr-2 h-4 w-4" />
                Feature Overview
              </DropdownMenuItem>
              <SupportDialog 
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid="menu-contact-support" className="dark:text-gray-200 dark:hover:bg-gray-700">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Contact Support
                  </DropdownMenuItem>
                }
              />
              <DropdownMenuSeparator className="dark:bg-gray-700" />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400" data-testid="menu-logout">
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowDeleteDialog(true)} className="text-red-600 dark:text-red-400" data-testid="menu-delete-account">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Account
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Delete Account Dialog */}
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Account?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div>
                    {/* Trial / subscription warning — surfaces BEFORE the destructive
                        action so users in an active trial don't accidentally throw
                        it away. Only renders when relevant. */}
                    {(() => {
                      const trialEnds = user?.trial_ends_at;
                      const trialActive = trialEnds && new Date(trialEnds) > new Date();
                      const hasSub = !!user?.subscription_status && user.subscription_status !== "CANCELED";
                      if (!trialActive && !hasSub) return null;
                      const daysLeft = trialActive
                        ? Math.max(0, Math.ceil((new Date(trialEnds) - new Date()) / (24 * 60 * 60 * 1000)))
                        : null;
                      return (
                        <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-3 mb-3" data-testid="delete-account-trial-warning">
                          <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                            ⚠️ You'll lose your{trialActive ? ` ${user?.trial_tier || ""} trial` : " subscription"}
                          </p>
                          <p className="text-xs text-amber-800 mt-1">
                            {trialActive
                              ? `You have ${daysLeft} day${daysLeft === 1 ? "" : "s"} left on your ${user?.trial_tier || "Pro"} trial. Deleting now ends it immediately — you can't get it back.`
                              : "Your active subscription will be cancelled and your access ends immediately."}
                          </p>
                        </div>
                      );
                    })()}

                    <p>This action cannot be undone. This will permanently delete your account and all associated data including:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>All customers</li>
                      <li>All quotes and invoices</li>
                      <li>All settings and preferences</li>
                      <li>Any active subscriptions</li>
                    </ul>

                    {/* Reason capture — optional but strongly encouraged.
                        Flows into the deletion archive so we can learn
                        what drives churn. PII-stripped on the backend. */}
                    <div className="mt-4">
                      <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                        Mind sharing why? (optional — helps us improve)
                      </label>
                      <textarea
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        placeholder="e.g. too expensive, missing feature X, broken Y, found a different tool…"
                        rows={3}
                        maxLength={500}
                        className="w-full text-sm border rounded-md p-2 bg-white dark:bg-gray-900 dark:border-gray-600 resize-none"
                        data-testid="delete-account-reason-input"
                      />
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setDeleteReason("")}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="delete-account-confirm-btn"
                >
                  {isDeleting ? "Deleting..." : "Yes, Delete My Account"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Main Content
          Mobile padding-top must clear the fixed mobile header. The header
          height is ~72px (h-12 logo + py-3 vertical padding). Using pt-20
          (80px) gives a comfortable safe margin and prevents page subheaders
          (e.g. ProjectEditor "P00002 / Draft" badge row) from being clipped. */}
      <div className="flex-1 overflow-auto pt-20 lg:pt-0 bg-gray-50 dark:bg-gray-900" data-tour="dashboard">
        <PaymentFailedBanner />
        <OnboardingNudge />
        <VoiceAllowanceIntroModal />
        <CalendarWatchNotice />
        <Outlet />
        <WhatsNewPopup />
      </div>

      {/* Trial Expired Modal */}
      {trialExpired && (
        <TrialExpiredModal
          user={user}
          onAction={(action) => {
            setTrialExpired(false);
            if (action === "reverted") {
              window.location.reload();
            }
          }}
        />
      )}

      {/* Walkthrough Guide Modal */}
      <WalkthroughGuide open={showWalkthrough} onOpenChange={setShowWalkthrough} />
      
      {/* Guided Tour */}
      <GuidedTour open={showGuidedTour} onOpenChange={setShowGuidedTour} />
    </div>
  );
};

export default Layout;
